import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isoDate,
  vehicleStatusLabels,
  vehicleTypeLabels,
  type Vehicle,
  type VehicleFuelType,
  type VehicleInput,
  type VehicleStatus,
  type VehicleTransmission,
  type VehicleType,
} from "@/hooks/useVehicles";

const TEXT_FIELDS = [
  "name",
  "brand",
  "model",
  "licensePlate",
  "registrationNumber",
  "vin",
  "licenseCategory",
  "notes",
] as const;
const DECIMAL_FIELDS = [
  "lengthM",
  "widthM",
  "heightM",
  "cargoLengthM",
  "cargoWidthM",
  "cargoHeightM",
] as const;
const INTEGER_FIELDS = [
  "emptyWeightKg",
  "grossWeightKg",
  "payloadKg",
  "towingCapacityKg",
  "seats",
  "mileageKm",
  "nextServiceKm",
] as const;
const DATE_FIELDS = [
  "firstRegistration",
  "nextInspectionDate",
  "nextServiceDate",
  "vignetteValidUntil",
  "insuranceValidUntil",
] as const;

type TextField = (typeof TEXT_FIELDS)[number];
type NumberField =
  | (typeof DECIMAL_FIELDS)[number]
  | (typeof INTEGER_FIELDS)[number];
type DateField = (typeof DATE_FIELDS)[number];

/** Form state: every input as a string, plus selects and switches */
type FormValues = Record<TextField | NumberField | DateField, string> & {
  vehicleType: VehicleType;
  status: VehicleStatus;
  fuelType: VehicleFuelType | "";
  transmission: VehicleTransmission | "";
  hasTowHitch: boolean;
  hasPowerInverter: boolean;
};

function toFormValues(vehicle?: Vehicle | null): FormValues {
  const values = {} as FormValues;
  for (const field of TEXT_FIELDS) values[field] = vehicle?.[field] ?? "";
  for (const field of [...DECIMAL_FIELDS, ...INTEGER_FIELDS]) {
    const value = vehicle?.[field];
    values[field] = value === null || value === undefined ? "" : String(value);
  }
  for (const field of DATE_FIELDS)
    values[field] = isoDate(vehicle?.[field]) ?? "";
  values.vehicleType = vehicle?.vehicleType ?? "van";
  values.status = vehicle?.status ?? "active";
  values.fuelType = vehicle?.fuelType ?? "";
  values.transmission = vehicle?.transmission ?? "";
  values.hasTowHitch = vehicle?.hasTowHitch ?? false;
  values.hasPowerInverter = vehicle?.hasPowerInverter ?? false;
  return values;
}

function parseNumber(value: string, integer: boolean) {
  const trimmed = value.trim().replace(",", ".");
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return integer ? Math.round(parsed) : parsed;
}

function toInput(values: FormValues): VehicleInput & { name: string } {
  const input: Record<string, unknown> = {};
  for (const field of TEXT_FIELDS) {
    input[field] = values[field].trim() || null;
  }
  input.name = values.name.trim();
  for (const field of DECIMAL_FIELDS)
    input[field] = parseNumber(values[field], false);
  for (const field of INTEGER_FIELDS)
    input[field] = parseNumber(values[field], true);
  for (const field of DATE_FIELDS) input[field] = values[field] || null;
  input.vehicleType = values.vehicleType;
  input.status = values.status;
  input.fuelType = values.fuelType || null;
  input.transmission = values.transmission || null;
  input.hasTowHitch = values.hasTowHitch;
  input.hasPowerInverter = values.hasPowerInverter;
  return input as VehicleInput & { name: string };
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-4">
      <legend className="text-sm font-semibold text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </fieldset>
  );
}

interface VehicleFormProps {
  vehicle?: Vehicle | null;
  onSubmit: (input: VehicleInput & { name: string }) => void;
  onCancel?: () => void;
  submitLabel: string;
  isPending?: boolean;
  /** Only the general and registration fields, e.g. for creating */
  compact?: boolean;
}

export function VehicleForm({
  vehicle,
  onSubmit,
  onCancel,
  submitLabel,
  isPending,
  compact = false,
}: VehicleFormProps) {
  const [values, setValues] = useState(() => toFormValues(vehicle));

  // Pick up changes synced from elsewhere when editing an existing vehicle
  const vehicleVersion = vehicle?.updatedAt;
  useEffect(() => {
    setValues(toFormValues(vehicle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.id, vehicleVersion]);

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const text = (
    field: TextField | NumberField,
    label: string,
    unit?: string,
  ) => (
    <div className="space-y-2">
      <Label htmlFor={`vehicle-${field}`}>
        {label}
        {unit && <span className="text-muted-foreground"> ({unit})</span>}
      </Label>
      <Input
        id={`vehicle-${field}`}
        value={values[field]}
        inputMode={
          (DECIMAL_FIELDS as readonly string[]).includes(field)
            ? "decimal"
            : (INTEGER_FIELDS as readonly string[]).includes(field)
              ? "numeric"
              : undefined
        }
        onChange={(event) => set(field, event.target.value)}
        data-testid={`vehicle-${field}-input`}
      />
    </div>
  );

  const date = (field: DateField, label: string) => (
    <div className="space-y-2">
      <Label htmlFor={`vehicle-${field}`}>{label}</Label>
      <DatePicker
        id={`vehicle-${field}`}
        value={values[field]}
        onChange={(value) => set(field, value)}
      />
    </div>
  );

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!values.name.trim()) return;
    onSubmit(toInput(values));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <Section title="General">
        {text("name", "Name")}
        {text("brand", "Brand")}
        {text("model", "Model / type")}
        <div className="space-y-2">
          <Label>Vehicle type</Label>
          <Select
            value={values.vehicleType}
            onValueChange={(value) => set("vehicleType", value as VehicleType)}
          >
            <SelectTrigger data-testid="vehicle-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(vehicleTypeLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            value={values.status}
            onValueChange={(value) => set("status", value as VehicleStatus)}
          >
            <SelectTrigger data-testid="vehicle-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(vehicleStatusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section title="Registration">
        {text("licensePlate", "License plate")}
        {text("registrationNumber", "Registration no. (Stammnummer)")}
        {text("vin", "VIN (chassis number)")}
        {date("firstRegistration", "First registration")}
        {text("licenseCategory", "Required licence", "e.g. B, BE, C1")}
      </Section>

      {!compact && (
        <>
          <Section title="Dimensions & weights">
            {text("lengthM", "Length", "m")}
            {text("widthM", "Width", "m")}
            {text("heightM", "Height", "m")}
            {text("cargoLengthM", "Cargo length", "m")}
            {text("cargoWidthM", "Cargo width", "m")}
            {text("cargoHeightM", "Cargo height", "m")}
            {text("emptyWeightKg", "Empty weight", "kg")}
            {text("grossWeightKg", "Gross weight", "kg")}
            {text("payloadKg", "Payload", "kg")}
            {text("towingCapacityKg", "Towing capacity", "kg")}
            {text("seats", "Seats")}
          </Section>

          <Section title="Technical">
            <div className="space-y-2">
              <Label>Fuel</Label>
              <Select
                value={values.fuelType || "none"}
                onValueChange={(value) =>
                  set(
                    "fuelType",
                    value === "none" ? "" : (value as VehicleFuelType),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">–</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="petrol">Petrol</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Transmission</Label>
              <Select
                value={values.transmission || "none"}
                onValueChange={(value) =>
                  set(
                    "transmission",
                    value === "none" ? "" : (value as VehicleTransmission),
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">–</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="automatic">Automatic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {text("mileageKm", "Mileage", "km")}
            <div className="flex items-center gap-3 pt-6">
              <Switch
                id="vehicle-tow-hitch"
                checked={values.hasTowHitch}
                onCheckedChange={(checked) => set("hasTowHitch", checked)}
              />
              <Label htmlFor="vehicle-tow-hitch">Tow hitch</Label>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                id="vehicle-power-inverter"
                checked={values.hasPowerInverter}
                onCheckedChange={(checked) => set("hasPowerInverter", checked)}
              />
              <Label htmlFor="vehicle-power-inverter">
                230 V power / inverter
              </Label>
            </div>
          </Section>

          <Section title="Deadlines">
            {date("nextInspectionDate", "Next MFK")}
            {date("nextServiceDate", "Next service")}
            {text("nextServiceKm", "Next service at", "km")}
            {date("vignetteValidUntil", "Vignette valid until")}
            {date("insuranceValidUntil", "Insurance / leasing until")}
          </Section>

          <div className="space-y-2">
            <Label htmlFor="vehicle-notes">Notes</Label>
            <Textarea
              id="vehicle-notes"
              value={values.notes}
              onChange={(event) => set("notes", event.target.value)}
              className="min-h-[100px]"
            />
          </div>
        </>
      )}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          offlineEdit
          disabled={!values.name.trim() || isPending}
          data-testid="save-vehicle"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
